// client/src/components/CourseEdit.js
// Course creation and editing component for superadmins

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HeadlineMedium, LabelMedium } from './Typography';
import { db } from '../firebase';
import { doc, getDoc, setDoc, addDoc, collection } from 'firebase/firestore';
import { getAdminUrl } from '../utils/urlUtils';
import Toast from './Toast';

const CourseEdit = ({ onBackToCourses }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Form state
  const [formData, setFormData] = useState({
    course_name: '',
    course_headline: '',
    course_description: '',
    hours_to_complete: '',
    lessons_count: '',
    activities_count: '',
    type: 'HOW TO WRITE A'
  });

  // Toast helper functions
  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  // Load course data
  useEffect(() => {
    const loadCourseData = async () => {
      if (!courseId) {
        // Creating new course - set default values
        setFormData({
          course_name: '',
          course_headline: '',
          course_description: '',
          hours_to_complete: '',
          lessons_count: '',
          activities_count: '',
          type: 'HOW TO WRITE A'
        });
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);

        // Fetch existing course details
        const courseRef = doc(db, 'courses', courseId);
        const courseSnapshot = await getDoc(courseRef);
        
        if (courseSnapshot.exists()) {
          const courseData = courseSnapshot.data();
          setFormData({
            course_name: courseData.course_name || '',
            course_headline: courseData.course_headline || '',
            course_description: courseData.course_description || '',
            hours_to_complete: courseData.hours_to_complete || '',
            lessons_count: courseData.lessons_count || '',
            activities_count: courseData.activities_count || '',
            type: courseData.type || 'HOW TO WRITE A'
          });
        } else {
          showToast('Course not found', 'error');
        }

      } catch (error) {
        console.error('Error loading course data:', error);
        showToast('Failed to load course data', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadCourseData();
  }, [courseId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Rich text editor functions
  const applyFormat = (format) => {
    const textarea = document.getElementById('course-description');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
    setFormData(prev => ({ ...prev, course_description: newValue }));
    
    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  };

  const handleSave = async () => {
    try {
      setSaving(true);


      // Validation - require name, headline, and description
      if (!formData.course_name.trim()) {
        showToast('Course name is required', 'error');
        return;
      }

      if (!formData.course_headline.trim()) {
        showToast('Course headline is required', 'error');
        return;
      }

      if (!formData.course_description.trim()) {
        showToast('Course description is required', 'error');
        return;
      }

      // Prepare course data
      const courseData = {
        course_name: formData.course_name.trim(),
        course_headline: formData.course_headline.trim(),
        course_description: formData.course_description.trim(),
        hours_to_complete: parseInt(formData.hours_to_complete) || 0,
        lessons_count: parseInt(formData.lessons_count) || 0,
        activities_count: parseInt(formData.activities_count) || 0,
        type: formData.type.trim(),
        title: formData.course_name.trim(), // Set title same as course_name
        updatedAt: new Date().toISOString()
      };

      if (courseId) {
        // Update existing course
        const courseRef = doc(db, 'courses', courseId);
        await setDoc(courseRef, courseData, { merge: true });
        showToast('Course updated successfully!', 'success');

        // Refresh course data
        const updatedCourseSnapshot = await getDoc(courseRef);
        if (updatedCourseSnapshot.exists()) {
          // Course updated successfully
        }
      } else {
        // Create new course
        courseData.createdAt = new Date().toISOString();
        courseData.course_id = `STORYKIN_${formData.course_name.toUpperCase().replace(/\s+/g, '_')}`;
        
        const newCourseRef = await addDoc(collection(db, 'courses'), courseData);
        showToast('Course created successfully!', 'success');
        
        // Course created successfully
      }

    } catch (error) {
      console.error('Error saving course:', error);
      showToast('Failed to save course', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading course...</span>
      </div>
    );
  }

  const pageTitle = courseId ? 'Course Edit' : 'Create New Course';
  const saveButtonText = saving ? 'Saving...' : (courseId ? 'Save Changes' : 'Create Course');

  return (
    <div className="space-y-8">
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={hideToast}
        duration={3000}
      />

      {/* Back Button */}
      {onBackToCourses && (
        <div className="mb-4">
          <button
            onClick={onBackToCourses}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Courses
          </button>
        </div>
      )}

      {/* Course Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <HeadlineMedium color="gray-900">{pageTitle}</HeadlineMedium>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saveButtonText}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Course Name */}
          <div className="md:col-span-2">
            <LabelMedium color="gray-700">Course Name *</LabelMedium>
            <input
              type="text"
              value={formData.course_name}
              onChange={(e) => handleInputChange('course_name', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter course name"
            />
          </div>

          {/* Course Headline */}
          <div className="md:col-span-2">
            <LabelMedium color="gray-700">Course Headline *</LabelMedium>
            <input
              type="text"
              value={formData.course_headline}
              onChange={(e) => handleInputChange('course_headline', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter course headline"
            />
          </div>

          {/* Course Description */}
          <div className="md:col-span-2">
            <LabelMedium color="gray-700">Course Description *</LabelMedium>
            <div className="mt-1">
              {/* Rich Text Toolbar */}
              <div className="flex space-x-2 mb-2 p-2 bg-gray-50 border border-gray-300 rounded-t-md">
                <button
                  type="button"
                  onClick={() => applyFormat('bold')}
                  className="px-3 py-1 text-sm font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                  title="Bold"
                >
                  B
                </button>
                <button
                  type="button"
                  onClick={() => applyFormat('italic')}
                  className="px-3 py-1 text-sm italic bg-white border border-gray-300 rounded hover:bg-gray-100"
                  title="Italic"
                >
                  I
                </button>
                <button
                  type="button"
                  onClick={() => applyFormat('underline')}
                  className="px-3 py-1 text-sm underline bg-white border border-gray-300 rounded hover:bg-gray-100"
                  title="Underline"
                >
                  U
                </button>
              </div>
              
              {/* Text Area */}
              <textarea
                id="course-description"
                value={formData.course_description}
                onChange={(e) => handleInputChange('course_description', e.target.value)}
                rows={6}
                className="block w-full border border-gray-300 rounded-b-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter course description (use B/I/U buttons above to format selected text)"
              />
            </div>
          </div>

          {/* Hours to Complete */}
          <div>
            <LabelMedium color="gray-700">Hours to Complete</LabelMedium>
            <input
              type="number"
              value={formData.hours_to_complete}
              onChange={(e) => handleInputChange('hours_to_complete', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
              min="0"
            />
          </div>

          {/* Lessons Count */}
          <div>
            <LabelMedium color="gray-700">Number of Lessons</LabelMedium>
            <input
              type="number"
              value={formData.lessons_count}
              onChange={(e) => handleInputChange('lessons_count', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
              min="0"
            />
          </div>

          {/* Activities Count */}
          <div>
            <LabelMedium color="gray-700">Number of Activities</LabelMedium>
            <input
              type="number"
              value={formData.activities_count}
              onChange={(e) => handleInputChange('activities_count', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
              min="0"
            />
          </div>

          {/* Course Type */}
          <div>
            <LabelMedium color="gray-700">Course Type</LabelMedium>
            <input
              type="text"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., HOW TO WRITE A"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseEdit;
